import type { Runner, RunRequest, RunResult, RunProgress, RunHooks } from "./runner";
import { normalizeHooks } from "./runner";
import type { ChunkRequest, ChunkReply } from "./worker";
import { resolveJob, assembleResult, chunkSample } from "./compute";

// Chunk planning + ordered reduction live in ./compute so the determinism contract is
// shared with the headless test harness. Reproducibility depends on the number of chunks
// being a deterministic function of (kernel, N) ONLY — never of the worker-pool size.
// (Verified: tying chunks to worker count made 4-worker and 8-worker runs disagree. Don't
// reintroduce that.) The worker pool below is just a work queue over those fixed chunks.

/**
 * createLocalRunner — the M0 backend. Splits N into a fixed set of logical chunks
 * (count = ceil(N / CHUNK_SIZE), independent of hardware), processes them across a Web
 * Worker pool via a work queue, and reduces partials in fixed chunk-index order. The
 * result therefore depends only on (kernel, seed, N) and reproduces bitwise on any machine.
 *
 * Local twin of a future mesh `connect()`; both satisfy `Runner`.
 */
export function createLocalRunner(opts: { workers?: number } = {}): Runner {
  const poolSize = Math.max(
    1,
    opts.workers ?? (navigator.hardwareConcurrency || 4),
  );
  const pool: Worker[] = [];
  const spawn = () =>
    new Worker(new URL("./worker.ts", import.meta.url), { type: "module" });

  function ensurePool() {
    while (pool.length < poolSize) pool.push(spawn());
  }

  async function run(
    req: RunRequest,
    hooks?: RunHooks | ((p: RunProgress) => void),
  ): Promise<RunResult> {
    const { onProgress, onChunk } = normalizeHooks(hooks);
    // Resolve job (kernel + seed + params + chunk plan) via the shared core, so the local
    // and mesh runners can't diverge. For custom kernels this lints before any worker is
    // touched, so a nondeterministic kernel fails fast on submit.
    const job = await resolveJob(req);
    const { isCustom, jobSeed, params } = job;
    const { numChunks, countFor } = job.plan;
    ensurePool();

    const partials = new Array<unknown>(numChunks);
    let next = 0; // next logical chunk index to dispatch
    let done = 0;
    let completed = 0;
    const t0 = performance.now();

    // Each worker pulls the next chunk off the shared counter until the queue drains.
    await new Promise<void>((resolve, reject) => {
      const dispatch = (worker: Worker) => {
        if (next >= numChunks) return;
        const chunkIndex = next++;
        const count = countFor(chunkIndex);
        const msg: ChunkRequest = {
          kernelId: req.kernelId,
          chunkIndex,
          count,
          jobSeed,
          params,
          ...(isCustom ? { source: req.source } : {}),
        };
        worker.postMessage(msg);
      };

      pool.forEach((worker, workerId) => {
        worker.onmessage = (e: MessageEvent<ChunkReply>) => {
          if (e.data.error) {
            reject(new Error(`Kernel error in chunk ${e.data.chunkIndex}: ${e.data.error}`));
            return;
          }
          const { chunkIndex, partial } = e.data;
          partials[chunkIndex] = partial;
          done++;
          const count = countFor(chunkIndex);
          completed += count;
          onProgress?.({ completed, total: req.n });
          // Advisory live stream — `workerId` is this pool worker's stable index. Never
          // touches the canonical reduce, which runs over partials in fixed index order.
          onChunk?.({
            chunkIndex,
            numChunks,
            count,
            sample: chunkSample(job.kernel, partial, count),
            workerId,
            completed,
            total: req.n,
          });
          if (done >= numChunks) resolve();
          else dispatch(worker);
        };
        worker.onerror = (err) => reject(err);
      });
      // Prime: give each worker its first chunk.
      for (const worker of pool) dispatch(worker);
    });

    // Reduce in fixed chunk-index order — NOT completion order — for reproducibility, and
    // assemble the result via the shared core (identical for local and mesh).
    return assembleResult(req, job, partials, performance.now() - t0);
  }

  function dispose() {
    for (const w of pool) w.terminate();
    pool.length = 0;
  }

  return { run, dispose };
}
